<template>
  <div data-component-group="filter-forms" data-component="payment">
    <FiltersForm
      has-phone-sheet
      :applied-filters="appliedFilters"
      :on-clear-filter="clearFilter"
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <template v-slot:phone-actions>
        <slot name="phone-actions" />
      </template>

      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <v-autocomplete
        v-if="!isCreatorProgramPage"
        clearable
        v-model="contract"
        :items="contracts"
        :custom-filter="customIdFilter"
        :loading="isLoadingContracts"
        prepend-inner-icon="event"
        :return-object="true"
        :item-title="contractName"
        label="Price contract ID"
        v-model:search="contractSearch"
      />

      <v-autocomplete
        v-if="!isCreatorProgramPage"
        clearable
        v-model="advertiser"
        :items="advertisers"
        :loading="isLoadingAdvertisers"
        prepend-inner-icon="add_business"
        :return-object="true"
        item-title="name"
        :label="$t('advertiser')"
        v-model:search="advertiserSearch"
      />

      <base-date-picker
        :date="date"
        placeholder="Created at"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        label="Business"
        item-title="name"
        item-value="id"
        v-model:search="businessSearch"
        @click:clear="getBusinesses()"
        @focus="!businesses?.length && getBusinesses()"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import filterUrlSync from '@/mixins/filterUrlSync';
  import filterChips, { platformChips } from '@/mixins/filterChips';
  import isDateRange from '../../../validators/dateRange';
  import { isNum, isStr, isNull, debounce, escapeSearch } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500,
  });

  // TODO: Translate component
  export default {
    mixins: [filterUrlSync, filterChips],
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        urlConfig: [
          { field: 'search', urlKey: 'search', type: 'string' },
          { field: 'selectedCountries', urlKey: 'countries', type: 'string[]' },
          { field: 'date', urlKey: 'date', type: 'string' },
          {
            // Looked up by id: a contract restored from the URL may sit
            // outside the first page of getContracts(), and it has to end
            // up among the options.
            field: 'contract', urlKey: 'contract', type: 'number', prop: 'id',
            fetchByIds: 'getContractsByIds',
          },
          {
            field: 'advertiser', urlKey: 'advertiser', type: 'number', prop: 'id',
            fetchByIds: 'getAdvertisersByIds',
          },
          { field: 'business', urlKey: 'business', type: 'number', fetchByIds: 'getBusinessesByIds' },
        ],
        search: null,
        contract: null,
        advertiser: null,
        contractSearch: '',
        business: null,
        // business is a raw id (no return-object), so a restored value has no
        // object of its own to inject into `businesses` below — this holds
        // the object getBusinessesByIds resolves it to, once that settles.
        restoredBusiness: null,
        businessSearch: '',
        advertiserSearch: '',
        date: null,
        selectedCountries: [],
        dateConfig: {
          mode: 'range',
        },
        isLoadingContracts: false,
        isRefreshingContracts: false,
        isLoadingBusinesses: false,
        isRefreshingBusinesses: false,
        isLoadingAdvertisers: false,
        isRefreshingAdvertisers: false,
      };
    },
    computed: {
      filterChipFields() {
        const platforms = this.$store.getters['users/countries'] || [];

        return [
          {
            model: 'selectedCountries',
            label: this.$t('platform'),
            toChips: platformChips(platforms),
          },
          { model: 'search', label: 'Search' },
          {
            model: 'contract',
            label: 'Price contract ID',
            toChips: (contract) => [{ value: contract?.id }],
          },
          { model: 'advertiser', label: this.$t('advertiser') },
          { model: 'date', label: 'Created at' },
          {
            model: 'business',
            label: 'Business',
            toChips: (business) => [{
              value: this.businesses.find(({ id }) => id === business)?.name ?? business,
            }],
          },
        ];
      },
      contracts() {
        const items = this.$store.getters['contracts/items'];
        // A contract restored from the URL is looked up by id, so it is not in
        // the listing the store holds. Keep it among the options.
        return this.contract && !items.some(({ id }) => id === this.contract.id)
          ? [...items, this.contract]
          : items;
      },
      advertisers() {
        const items = this.$store.getters['promotions/advertisers'];
        // Same reasoning as contracts above.
        return this.advertiser && !items.some(({ id }) => id === this.advertiser.id)
          ? [...items, this.advertiser]
          : items;
      },
      hasFilters() {
        const {
          search,
          contract = {},
          advertiser = {},
          selectedCountries = [],
          date,
          business
        } = this;

        return (
          !!search ||
          isNum(contract?.id) ||
          !!date ||
          selectedCountries.length > 0 ||
          isNum(advertiser?.id) ||
          isNum(business)
        );
      },
      isCreatorProgramPage() {
        return this.$route.path.includes('/creator-program');
      },
      businesses() {
        const items = this.isCreatorProgramPage ? this.$store.getters['creatorProgram/businesses'] : this.$store.getters['businesses/items'];
        return this.restoredBusiness && !items.some(({ id }) => id === this.restoredBusiness.id)
          ? [...items, this.restoredBusiness]
          : items;
      },
    },
    watch: {
      contract() {
        this.isRefreshingContracts = true;
      },
      advertiser() {
        this.isRefreshingAdvertiser = true;
      },
      business() {
        this.isRefreshingBusinesses = true;
      },
      businessSearch(nextSearch) {
        if (this.isRefreshingBusinesses) {
          this.isRefreshingBusinesses = false;
          return false;
        }

        if (isStr(nextSearch)) {
          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      contractSearch(nextSearch) {
        if (!isNull(nextSearch)) {
          if (this.isRefreshingContracts) {
            this.isRefreshingContracts = false;
            return false;
          }

          debouncer(() => this.getContracts(nextSearch));
        }
      },
      advertiserSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingAdvertiser) {
            this.isRefreshingAdvertiser = false;
            return false;
          }

          debouncer(() => this.getAdvertisers(nextSearch));
        }
      },
    },
    methods: {
      getContractsByIds(ids) {
        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItemsByIds', { ids })
          .finally(() => {
            this.isLoadingContracts = false;
          });
      },
      getAdvertisersByIds(ids) {
        this.isLoadingAdvertisers = true;
        return this.$store
          .dispatch('promotions/getAdvertisersByIds', { ids })
          .finally(() => {
            this.isLoadingAdvertisers = false;
          });
      },
      getBusinessesByIds(ids) {
        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItemsByIds', { ids })
          .then((items) => {
            this.restoredBusiness = items[0] || null;
            return items;
          })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getBusinesses(search = '') {
        const query = {
          per_page: this.isCreatorProgramPage ? null : -1,
          search,
        };

        this.isLoadingBusinesses = true;

        const action = this.isCreatorProgramPage ? 'creatorProgram/getBusinesses' : 'businesses/getItems';

        return this.$store.dispatch(action, query)
          .catch(err => err)
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getContracts(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItems', query)
          .then((res) => {
            this.isLoadingContracts = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingContracts = false;
            return err;
          });
      },
      getAdvertisers(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingAdvertisers = true;

        return this.$store
          .dispatch('promotions/getAdvertisers', query)
          .then((res) => {
            this.isLoadingAdvertisers = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingAdvertisers = false;
            return err;
          });
      },
      contractName(item) {
        return item.id ? item.id.toString() : '';
      },
      onFilter() {
        const {
          search,
          contract = {},
          advertiser = {},
          date,
          selectedCountries = [],
          business
        } = this;
        const formattedSearch = escapeSearch(search);
        const hasDate = !!date;
        let nextDate = date;

        if (hasDate && !isDateRange(nextDate)) {
          nextDate = `${nextDate} - ${nextDate}`;
        }

        this.syncFiltersToUrl();
        this.rememberAppliedFilters();

        this.$emit('filter', {
          search: formattedSearch,
          contract_id: contract?.id,
          advertiser_id: advertiser?.id,
          countries: selectedCountries,
          created_at: nextDate,
          business_id: business,
        });
      },
      onClearFilters() {
        this.search = null;
        this.contract = null;
        this.advertiser = null;
        this.selectedCountries = [];
        this.date = null;
        this.business = null;
        this.restoredBusiness = null;

        this.clearFiltersUrl();
        this.forgetAppliedFilters();

        this.$emit('clearFilters');
      },
      // todo: delete this method when not needed
      customIdFilter(value, queryText) {
        if (!value || !queryText) return false;
        return value.toString().toLowerCase().indexOf(queryText.toString().toLowerCase()) > -1;
      },
      onChangeDate(date) {
        this.date = date;
      },
    },
  };
</script>
